<template>
  <Dialog
    :visible="show"
    modal
    :closable="!isSaving"
    :close-on-escape="!isSaving"
    :dismissable-mask="false"
    @update:visible="(visible) => { if (!visible) emit('cancel'); }"
    :style="{ width: '28rem' }"
    :pt="{ root: { class: 'rounded-lg' } }"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <span class="material-icons text-yellow-400 select-none">warning</span>
        <span class="font-semibold">{{ $t('flowEditor.unsavedChangesModal.title') }}</span>
      </div>
    </template>

    <p class="text-gray-300">
      {{ $t('flowEditor.unsavedChangesModal.message') }}
    </p>

    <template #footer>
      <Button
        severity="secondary"
        :label="$t('flowEditor.unsavedChangesModal.discardButton')"
        :disabled="isSaving"
        @click="emit('discard')"
      />
      <!-- The icon goes in the #icon slot (not the default slot) so that
           :loading can swap it for PrimeVue's built-in spinner; the default
           slot would replace the whole content and suppress the spinner. -->
      <Button
        :label="isSaving ? $t('flowEditor.header.savingButton') : $t('flowEditor.unsavedChangesModal.saveButton')"
        :loading="isSaving"
        :disabled="isSaving"
        @click="emit('save')"
      >
        <template #icon>
          <span class="material-icons text-base select-none">save</span>
        </template>
      </Button>
    </template>
  </Dialog>
</template>

<script setup>
/**
 * UnsavedChangesModal.vue
 *
 * Confirm dialog shown when leaving the flow editor with unsaved changes:
 * Save or Discard, or close it (× / Escape, not while saving) to stay in
 * the editor. A click on the mask does not close it.
 *
 * @example
 * <UnsavedChangesModal :show="showModal" :is-saving="isSaving" @save="onSave" @discard="onDiscard" @cancel="onStay" />
 */

defineProps({
  /** Whether the dialog is visible */
  show: {
    type: Boolean,
    default: false,
  },
  /** Save in progress: shows the loading state and disables both buttons */
  isSaving: {
    type: Boolean,
    default: false,
  },
});

/**
 * @event save - User chose to save the changes
 * @event discard - User chose to discard the changes
 * @event cancel - User closed the dialog (× or Escape) to stay in the editor
 */
const emit = defineEmits(['save', 'discard', 'cancel']);
</script>
